import type { TechlineCaseEntity } from '@packages/domain/techline/cases';
import type { KeyboardEvent } from 'react';

import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';
import { Button, Typography } from '@packages/ui/shared';
import { analyticsService } from '@web/services/singletons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { WorksheetSubmissionGroup } from '../../techline-draft-stepper.types';

import {
    type WorksheetGroup,
    WorksheetGroupAccordion,
    type WorksheetOpenMode,
} from '../../../../../worksheet-group-accordion';
import { DeleteWorksheetModal } from './delete-worksheet-modal';

const logger = getLogger('techline');

/** State for the delete worksheet confirmation modal. */
type DeleteModalState = {
    /** Whether the delete modal is visible. */
    isVisible: boolean;

    /** The submission ID to delete. */
    submissionId: string;

    /** The worksheet ID being deleted. */
    worksheetId: string;

    /** The display name of the worksheet being deleted. */
    worksheetTitle: string;
};

const INITIAL_DELETE_MODAL_STATE: DeleteModalState = {
    isVisible: false,
    submissionId: '',
    worksheetId: '',
    worksheetTitle: '',
};

/** Props for the WorksheetsStepContent component. */
export type WorksheetsStepContentProps = {
    /** The case details for the current draft case. */
    caseDetails?: TechlineCaseEntity;

    /** The case number being edited. */
    caseNumber: string;

    /** ID of a worksheet to scroll to and focus after returning from the worksheet form. */
    focusWorksheetId?: string | null;

    /** Callback to navigate back to the previous stepper step. */
    goBack?: () => void;

    /** Whether worksheet groups are currently loading. Defaults to false. */
    isLoadingGroups?: boolean;

    /** Whether worksheets are being initialized (e.g., loading titles after returning from form). Defaults to false. */
    isInitializingWorksheets?: boolean;

    /** Callback when case details change (e.g., after worksheet deletion). */
    onCaseDetailsChange?: (data: Partial<TechlineCaseEntity>) => void;

    /** Callback to clear the focus worksheet ID after scrolling is complete. */
    onClearFocusWorksheet?: () => void;

    /** Callback to delete a worksheet submission. Returns true on success. */
    onDeleteWorksheet?: (submissionId: string) => Promise<boolean>;

    /** Callback to load worksheet titles for a specific group. */
    onLoadWorksheetTitles?: (groupId: string) => void;

    /** Callback to open a worksheet (new or existing). */
    onOpenWorksheet?: (worksheetId: string, submissionId: string | undefined, mode: WorksheetOpenMode) => void;

    /** Callback when a field is touched. */
    onTouched: (field: string) => void;

    /** Error message to display when group loading fails. Defaults to empty string. */
    worksheetGroupError?: string;

    /** The loaded worksheet groups with their titles. Defaults to empty array. */
    worksheetGroupData?: WorksheetGroup[];

    /** Worksheet submission groups from the case details. */
    worksheetSubmissionGroups?: WorksheetSubmissionGroup[];

    /** Validation results for draft worksheets. Keyed by worksheetId, true if valid. Defaults to empty object. */
    worksheetValidationResults?: Record<string, boolean>;
};

/**
 * Step content for the "Worksheets" step of the draft case stepper.
 * Displays worksheet groups as accordions and allows the user to select, complete,
 * edit, and delete worksheets. All data fetching and mutations are delegated
 * to the parent via callback props.
 *
 * @param props - {@link WorksheetsStepContentProps}
 * @returns the worksheets step content with group accordions
 */
export function WorksheetsStepContent({
    caseDetails,
    caseNumber,
    focusWorksheetId,
    goBack,
    isInitializingWorksheets = false,
    isLoadingGroups = false,
    onCaseDetailsChange,
    onClearFocusWorksheet,
    onDeleteWorksheet,
    onLoadWorksheetTitles,
    onOpenWorksheet,
    onTouched,
    worksheetGroupData = [],
    worksheetGroupError = '',
    worksheetSubmissionGroups,
    worksheetValidationResults = {},
}: WorksheetsStepContentProps) {
    // Hooks
    const { t } = useTranslation();
    const classes = useStyles();

    // State
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [selectedWorksheets, setSelectedWorksheets] = useState<string[]>([]);
    const [hasInitializedSelections, setHasInitializedSelections] = useState(false);
    const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>(INITIAL_DELETE_MODAL_STATE);
    const [isDeleteWorksheetLoading, setIsDeleteWorksheetLoading] = useState(false);
    const focusPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const prevFocusWorksheetIdRef = useRef<string | null | undefined>(null);
    const prevSubmissionGroupsRef = useRef(worksheetSubmissionGroups);

    // Derived state

    /**
     * Whether any worksheet group is still loading titles or the component is initializing.
     */
    const isLoadingWorksheets = useMemo(() => {
        return worksheetGroupData.some((group) => group.isLoading) || isInitializingWorksheets;
    }, [worksheetGroupData, isInitializingWorksheets]);

    /**
     * Whether any worksheet across all groups has a submission.
     */
    const hasAnyWorksheetSubmitted = useMemo(() => {
        return worksheetGroupData.some((g) => g.worksheetTitles.some((w) => Boolean(w.submissionId)));
    }, [worksheetGroupData]);

    /**
     * Sorts worksheet groups by selection and then by title to bring current selection to top.
     */
    const sortedWorksheetGroupData = useMemo(() => {
        return [...worksheetGroupData].sort(
            (a, b) =>
                Number(selectedGroups.includes(b.worksheetCaseGroupId)) -
                    Number(selectedGroups.includes(a.worksheetCaseGroupId)) || a.groupName.localeCompare(b.groupName)
        );
    }, [worksheetGroupData, selectedGroups]);

    // Handlers

    /**
     * Handles toggling a worksheet group's selection state and triggers title loading.
     *
     * @param groupId - the group ID being toggled
     * @param isSelected - whether the group is now selected
     */
    const handleGroupSelection = useCallback(
        (groupId: string, isSelected: boolean) => {
            if (isSelected) {
                setSelectedGroups((prev) => [...prev, groupId]);
                // Load worksheet titles if the group hasn't been loaded yet
                const group = worksheetGroupData.find((g) => g.worksheetCaseGroupId === groupId);
                if (group && !group.worksheetsLoaded && !group.isLoading) {
                    onLoadWorksheetTitles?.(groupId);
                }
            } else {
                setSelectedGroups((prev) => prev.filter((id) => id !== groupId));
            }
            onTouched('selectedGroups');
        },
        [worksheetGroupData, onLoadWorksheetTitles, onTouched]
    );

    /**
     * Handles toggling a worksheet's selection state.
     *
     * @param worksheetId - the worksheet ID being toggled
     * @param isSelected - whether the worksheet is now selected
     */
    const handleWorksheetSelection = useCallback(
        (worksheetId: string, isSelected: boolean) => {
            if (isSelected) {
                setSelectedWorksheets((prev) => [...prev, worksheetId]);
            } else {
                setSelectedWorksheets((prev) => prev.filter((id) => id !== worksheetId));
            }
            onTouched('selectedWorksheets');
        },
        [onTouched]
    );

    /**
     * Handles the "Complete" action on a worksheet (opens worksheet form for new completion).
     *
     * @param worksheetId - the worksheet to complete
     */
    const handleCompleteWorksheetButtonClick = useCallback(
        (worksheetId: string) => {
            analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_COMPLETE_WORKSHEET_CLICKED, {
                worksheetId,
                caseNumber,
            });
            onOpenWorksheet?.(worksheetId, undefined, 'add');
            return Promise.resolve();
        },
        [caseNumber, onOpenWorksheet]
    );

    /**
     * Handles the "Edit" action on a worksheet (opens worksheet form for an existing submission).
     *
     * @param worksheetId - the worksheet to edit
     * @param submissionId - the existing submission to edit
     */
    const handleEditWorksheetButtonClick = useCallback(
        (worksheetId: string, submissionId: string) => {
            analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_EDIT_WORKSHEET_CLICKED, {
                worksheetId,
                caseNumber,
                submissionId,
            });
            onOpenWorksheet?.(worksheetId, submissionId, 'edit');
            return Promise.resolve();
        },
        [caseNumber, onOpenWorksheet]
    );

    /**
     * Opens the delete confirmation modal for a worksheet.
     *
     * @param worksheetId - the worksheet to delete
     * @param submissionId - the submission to delete
     */
    const handleDeleteWorksheetButtonClick = useCallback(
        (worksheetId: string, submissionId: string) => {
            // Find the worksheet title for the confirmation modal
            let worksheetTitle = '';
            for (const group of worksheetGroupData) {
                const worksheet = group.worksheetTitles.find((w) => w.worksheetId === worksheetId);
                if (worksheet) {
                    worksheetTitle = worksheet.displayName;
                    break;
                }
            }

            setDeleteModalState({ isVisible: true, worksheetId, submissionId, worksheetTitle });
            analyticsService.logEvent(ANALYTICS_EVENTS.TECHLINE_DELETE_WORKSHEET_CLICKED, {
                worksheetId,
                worksheetTitle,
                caseNumber,
                submissionId,
            });
        },
        [caseNumber, worksheetGroupData]
    );

    /**
     * Confirms deletion of a worksheet submission by delegating to onDeleteWorksheet.
     * Updates local selection state and notifies the parent of case detail changes.
     */
    const handleConfirmDeleteButtonClick = useCallback(async () => {
        setIsDeleteWorksheetLoading(true);
        try {
            const success = await onDeleteWorksheet?.(deleteModalState.submissionId);

            if (success) {
                // Notify parent so case details state stays in sync
                if (onCaseDetailsChange) {
                    const deletedId = deleteModalState.submissionId;
                    onCaseDetailsChange({
                        worksheetSubmissions:
                            caseDetails?.worksheetSubmissions?.filter((ws) => ws.submissionId !== deletedId) ?? [],
                        WorksheetSubmissionGroups: worksheetSubmissionGroups
                            ?.map((group) => ({
                                ...group,
                                worksheetSubmissionTitles: group.worksheetSubmissionTitles?.filter(
                                    (ws) => ws.submissionId !== deletedId
                                ),
                            }))
                            .filter((group) => group.worksheetSubmissionTitles?.length > 0),
                    });
                }

                setSelectedWorksheets((prev) => prev.filter((id) => id !== deleteModalState.worksheetId));
            } else {
                alert('Failed to delete worksheet submission. Please try again.');
            }
        } catch (error) {
            logger.error(`Failed to delete worksheet submission [${deleteModalState.submissionId}]`, {
                submissionId: deleteModalState.submissionId,
                error,
            });
            alert('Error deleting worksheet submission. Please try again.');
        } finally {
            setIsDeleteWorksheetLoading(false);
        }

        setDeleteModalState(INITIAL_DELETE_MODAL_STATE);
    }, [deleteModalState, onDeleteWorksheet, onCaseDetailsChange, caseDetails, worksheetSubmissionGroups]);

    /**
     * Closes the delete confirmation modal without taking action.
     */
    const handleCancelDeleteButtonClick = useCallback(() => {
        setDeleteModalState(INITIAL_DELETE_MODAL_STATE);
    }, []);

    /**
     * Handles keyboard navigation (arrow keys, Home, End) for the worksheet group list.
     * Moves focus between accordion header buttons.
     *
     * @param e - the keyboard event from the list container
     */
    const handleListKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
            return;
        }

        e.preventDefault();

        const buttons = Array.from(e.currentTarget.querySelectorAll('button[aria-expanded]')) as HTMLElement[];
        if (buttons.length === 0) {
            return;
        }

        // Determine which button currently has focus
        const target = e.target as HTMLElement;
        let currentButton: HTMLElement | null = null;
        if (target.matches('button[aria-expanded]')) {
            currentButton = target;
        } else {
            const closestCard = target.closest('[class*="accordionCard"]');
            if (closestCard) {
                currentButton = closestCard.querySelector('button[aria-expanded]') as HTMLElement;
            }
        }

        if (!currentButton) {
            if (buttons.length > 0) {
                buttons[0].focus();
            }
            return;
        }

        const currentIndex = buttons.indexOf(currentButton);
        if (currentIndex === -1) {
            return;
        }

        // Compute next focus target based on key
        let nextIndex = currentIndex;
        if (e.key === 'ArrowDown') {
            nextIndex = Math.min(currentIndex + 1, buttons.length - 1);
        } else if (e.key === 'ArrowUp') {
            nextIndex = Math.max(currentIndex - 1, 0);
        } else if (e.key === 'Home') {
            nextIndex = 0;
        } else if (e.key === 'End') {
            nextIndex = buttons.length - 1;
        }

        if (nextIndex !== currentIndex && buttons[nextIndex]) {
            buttons[nextIndex].focus();
        }
    }, []);

    // Effects

    /**
     * Initializes selected groups and worksheets from worksheetSubmissionGroups
     * when worksheetGroupData first becomes available.
     */
    useEffect(() => {
        if (
            !worksheetSubmissionGroups ||
            worksheetSubmissionGroups.length === 0 ||
            hasInitializedSelections ||
            worksheetGroupData.length === 0
        ) {
            return;
        }

        const groupIds: string[] = [];
        const worksheetIds: string[] = [];

        worksheetSubmissionGroups.forEach((submissionGroup) => {
            if (submissionGroup.worksheetCaseGroupId) {
                groupIds.push(submissionGroup.worksheetCaseGroupId);

                // Load titles for groups that haven't been loaded yet
                const group = worksheetGroupData.find(
                    (g) => g.worksheetCaseGroupId === submissionGroup.worksheetCaseGroupId
                );
                if (group && !group.worksheetsLoaded && !group.isLoading) {
                    onLoadWorksheetTitles?.(submissionGroup.worksheetCaseGroupId);
                }
            }

            if (submissionGroup.worksheetSubmissionTitles && submissionGroup.worksheetSubmissionTitles.length > 0) {
                submissionGroup.worksheetSubmissionTitles.forEach((worksheet) => {
                    if (worksheet.worksheetId) {
                        worksheetIds.push(worksheet.worksheetId);
                    }
                });
            }
        });

        if (groupIds.length > 0) {
            setSelectedGroups(groupIds);
        }

        if (worksheetIds.length > 0) {
            setSelectedWorksheets(worksheetIds);
        }

        setHasInitializedSelections(true);
    }, [worksheetSubmissionGroups, hasInitializedSelections, worksheetGroupData, onLoadWorksheetTitles]);

    /**
     * Re-merges worksheet submission data when worksheetSubmissionGroups changes after
     * initial setup. Ensures newly saved worksheets and their groups are selected.
     */
    useEffect(() => {
        if (!hasInitializedSelections || worksheetSubmissionGroups === prevSubmissionGroupsRef.current) {
            prevSubmissionGroupsRef.current = worksheetSubmissionGroups;
            return;
        }
        prevSubmissionGroupsRef.current = worksheetSubmissionGroups;

        if (!worksheetSubmissionGroups || worksheetSubmissionGroups.length === 0) {
            return;
        }

        // Ensure newly saved worksheets and their groups are selected
        const allSubmittedWorksheetIds = worksheetSubmissionGroups.flatMap((group) =>
            group.worksheetSubmissionTitles.map((w) => w.worksheetId)
        );
        setSelectedWorksheets((prev) => {
            const newIds = allSubmittedWorksheetIds.filter((id) => !prev.includes(id));
            return newIds.length > 0 ? [...prev, ...newIds] : prev;
        });

        const groupsWithSubmissions = worksheetSubmissionGroups
            .filter((g) => g.worksheetSubmissionTitles.length > 0)
            .map((g) => g.worksheetCaseGroupId);
        setSelectedGroups((prev) => {
            const newIds = groupsWithSubmissions.filter((id) => !prev.includes(id));
            return newIds.length > 0 ? [...prev, ...newIds] : prev;
        });
    }, [worksheetSubmissionGroups, hasInitializedSelections]);

    /**
     * Scrolls to the specified worksheet when focusWorksheetId is set.
     * Polls for the DOM element since the accordion may still be animating open.
     */
    useEffect(() => {
        if (!isInitializingWorksheets && hasInitializedSelections && caseNumber && focusWorksheetId) {
            // Only process if the focusWorksheetId changed
            if (focusWorksheetId === prevFocusWorksheetIdRef.current) {
                return;
            }
            prevFocusWorksheetIdRef.current = focusWorksheetId;

            // Ensure the worksheet is selected (checkbox checked)
            setSelectedWorksheets((prev) => (prev.includes(focusWorksheetId) ? prev : [...prev, focusWorksheetId]));

            // Find parent group and ensure it's selected so the accordion expands
            let hasFoundGroup = false;
            for (const group of worksheetGroupData) {
                if (group.worksheetTitles.some((w) => w.worksheetId === focusWorksheetId)) {
                    hasFoundGroup = true;
                    setSelectedGroups((prev) =>
                        prev.includes(group.worksheetCaseGroupId) ? prev : [...prev, group.worksheetCaseGroupId]
                    );
                    break;
                }
            }

            // worksheetTitles not loaded yet; wait for the next worksheetGroupData update
            if (!hasFoundGroup) {
                return;
            }

            // Clear any previous polling interval before starting a new one
            if (focusPollIntervalRef.current) {
                clearInterval(focusPollIntervalRef.current);
                focusPollIntervalRef.current = null;
            }

            // Poll for the DOM element since the accordion may still be animating open
            let attempts = 0;
            const maxAttempts = 25;
            focusPollIntervalRef.current = setInterval(() => {
                attempts++;
                const card = document.querySelector(`[data-worksheet-id="${focusWorksheetId}"]`);
                if (card || attempts >= maxAttempts) {
                    if (focusPollIntervalRef.current) {
                        clearInterval(focusPollIntervalRef.current);
                        focusPollIntervalRef.current = null;
                    }
                    prevFocusWorksheetIdRef.current = null;
                    onClearFocusWorksheet?.();

                    if (card) {
                        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }, 200);
        }

        return () => {
            if (focusPollIntervalRef.current) {
                clearInterval(focusPollIntervalRef.current);
                focusPollIntervalRef.current = null;
            }
        };
    }, [
        isInitializingWorksheets,
        hasInitializedSelections,
        caseNumber,
        worksheetGroupData,
        focusWorksheetId,
        onClearFocusWorksheet,
    ]);

    return (
        <>
            <DeleteWorksheetModal
                isDeleting={isDeleteWorksheetLoading}
                isVisible={deleteModalState.isVisible}
                onCancel={handleCancelDeleteButtonClick}
                onConfirm={handleConfirmDeleteButtonClick}
                worksheetTitle={deleteModalState.worksheetTitle}
            />

            <div className={classes.container}>
                <div className={classes.headerContainer}>
                    <div className="pb-3 pt-4">
                        <Typography variant="h3">{t('techline:worksheets.header')}</Typography>
                    </div>
                    <div className={classes.headerSubtitle}>
                        <Typography variant="default">Please select a group that best describes the issue.</Typography>
                    </div>
                </div>

                {isLoadingGroups && (
                    <div className="mt-3">
                        <Typography>Loading worksheet groups...</Typography>
                    </div>
                )}

                {worksheetGroupError && (
                    <div className="mt-3">
                        <Typography color="redOne">{worksheetGroupError}</Typography>
                    </div>
                )}

                {!isLoadingGroups && !worksheetGroupError && worksheetGroupData.length > 0 && (
                    <>
                        {isLoadingWorksheets && (
                            <div className={classes.loadingSpinner}>
                                <div className={classes.spinner} />
                                <Typography color="grayFive" variant="default">
                                    Loading worksheets...
                                </Typography>
                            </div>
                        )}

                        <div onKeyDown={handleListKeyDown} role="list">
                            {sortedWorksheetGroupData.map((group) => {
                                return (
                                    <WorksheetGroupAccordion
                                        group={group}
                                        hasAnyWorksheetSubmitted={hasAnyWorksheetSubmitted}
                                        isDraftCase
                                        isSelected={selectedGroups.includes(group.worksheetCaseGroupId)}
                                        key={group.worksheetCaseGroupId}
                                        onCompleteWorksheet={handleCompleteWorksheetButtonClick}
                                        onDeleteWorksheet={handleDeleteWorksheetButtonClick}
                                        onEditWorksheet={handleEditWorksheetButtonClick}
                                        onGroupSelect={handleGroupSelection}
                                        onWorksheetSelect={handleWorksheetSelection}
                                        selectedWorksheets={selectedWorksheets}
                                        worksheetValidationResults={worksheetValidationResults}
                                    />
                                );
                            })}
                        </div>
                    </>
                )}

                {!isLoadingGroups && !worksheetGroupError && worksheetGroupData.length === 0 && (
                    <div className={classes.noGroupsContainer}>
                        <div className={classes.noGroupsTitle}>
                            <Typography variant="default">No worksheet groups are available at this time.</Typography>
                        </div>
                        <div className={classes.noGroupsSubtitle}>
                            <Typography color="grayFive" variant="caption">
                                Please return to the previous step and try again.
                            </Typography>
                        </div>
                        {goBack && (
                            <div className={classes.backButton}>
                                <Button onPress={goBack} variant="secondary">
                                    Back to Previous Step
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

const useStyles = createUseStyles({
    container: {
        minHeight: 0,
        padding: '16px 80px',
    },
    headerContainer: {
        display: 'block',
    },
    headerSubtitle: {
        marginTop: 12,
        paddingBottom: 8,
        display: 'block',
    },
    noGroupsContainer: {
        marginTop: 32,
        textAlign: 'center',
    },
    noGroupsTitle: {
        marginBottom: 16,
        display: 'block',
    },
    noGroupsSubtitle: {
        display: 'block',
    },
    backButton: {
        marginTop: 24,
    },
    loadingSpinner: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginTop: 16,
        marginBottom: 16,
    },
    spinner: {
        width: 20,
        height: 20,
        border: '3px solid #e0e0e0',
        borderTop: '3px solid #1976d2',
        borderRadius: '50%',
        animation: '$spin 1s linear infinite',
    },
    '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' },
    },
});
